import { useState, type ReactNode } from 'react';
import { AppModeContext, type AppMode } from './appMode';

const STORAGE_KEY = 'app-mode';
const ARABIC_KEY = 'arabic-names';

function readStoredMode(): AppMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'simple' ? 'simple' : 'full';
  } catch {
    return 'full';
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode etc. — keep in-memory value */
  }
}

// ponytail: per-device localStorage. Move to the worker's private doc if the choice
// must follow the worker across phones.
export function AppModeProvider({
  children,
  forced,
}: {
  children: ReactNode;
  /** Admin-assigned mode from the worker's roster entry; overrides the stored choice. */
  forced?: AppMode;
}) {
  const [stored, setModeState] = useState<AppMode>(readStoredMode);
  const mode = forced ?? stored;
  const setMode = (m: AppMode) => {
    writeStored(STORAGE_KEY, m);
    setModeState(m);
  };
  const [arabicNames, setArabicState] = useState(() => {
    try {
      return localStorage.getItem(ARABIC_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setArabicNames = (on: boolean) => {
    writeStored(ARABIC_KEY, on ? '1' : '0');
    setArabicState(on);
  };
  return (
    <AppModeContext.Provider
      value={{ mode, setMode, forced: forced !== undefined, arabicNames, setArabicNames }}
    >
      {children}
    </AppModeContext.Provider>
  );
}
